"use client"

import { useMemo, useState } from "react"
import { ArrowUpRight, Info, RefreshCw } from "lucide-react"
import {
  StatBorderGrid,
  ToolPageShell,
  ToolProductCarousel,
  ToolShareBar,
} from "@/components/brand-kit"
import { TOOL_METADATA } from "@/lib/tool-metadata"
import { readQuantity } from "@/lib/tools/numeric-input"

const FALLBACK_RELATED = [
  "refrigerator-compressor-amp-calculator",
  "appliance-energy-calculator",
  "energy-cost-calculator",
  "refrigerator-size-calculator",
]

function buildRelated(slugs: string[], currentSlug: string) {
  const seen = new Set<string>([currentSlug])
  const out: { title: string; category: string; description: string; href: string; time: string }[] = []
  for (const s of [...slugs, ...FALLBACK_RELATED]) {
    if (out.length === 4) break
    if (seen.has(s)) continue
    const m = TOOL_METADATA[s]
    if (!m) continue
    seen.add(s)
    out.push({
      title: m.name,
      category: m.category,
      description: m.shortDescription,
      href: `/tools/${s}`,
      time: "~30 sec",
    })
  }
  return out
}

const META = {
  name: "Refrigerator Generator Size Calculator",
  category: "Installation & Setup" as const,
  mediumDescription:
    "Size a backup generator to keep your fridge, freezer, and essentials running. Accounts for compressor surge wattage (typically 3× running watts) plus a 20% safety margin per portable-generator manufacturer guidance.",
  faqs: [
    { question: "What size generator do I need to run a fridge?", answer: "A typical full-size fridge runs at 100–800 watts but surges to 1,200–2,400 watts at startup. A 2,000–3,000W portable generator is the practical minimum for fridge-only backup." },
    { question: "Why are starting watts so much higher than running watts?", answer: "Single-speed reciprocating compressors draw 3–5× their running wattage for the first 1–3 seconds of startup as the motor overcomes inertia. Inverter compressors have softer starts (1.5–2× surge)." },
    { question: "Can I run a fridge and freezer on one generator?", answer: "Yes — sum running watts and account for the highest single starting surge (compressors don't typically start simultaneously). A 5,000W portable generator can usually handle both plus lights." },
    { question: "Do I need a transfer switch?", answer: "A manual transfer switch is required by NEC 702.5 for any generator wired into your home's panel. Plug-in extension cords from the generator to individual appliances do not require one." },
    { question: "How much fuel does a generator use?", answer: "A 5,000W portable running at 50% load uses roughly 0.5 gallons of gasoline per hour, per most manufacturer spec sheets — about 12 gallons per day of continuous use." },
    { question: "Should I get inverter or standby?", answer: "Inverter generators run quieter and more efficiently at partial loads — better for fridge-only use. Standby (whole-house) generators self-start during outages and can power AC, well pump, and HVAC." },
  ],
  relatedTools: ["refrigerator-compressor-amp-calculator", "appliance-energy-calculator", "energy-cost-calculator"],
}

export function GeneratorSizeCalculatorPageClient() {
  const [fridgeWatts, setFridgeWatts] = useState("700")
  const [freezerWatts, setFreezerWatts] = useState("500")
  const [includeAC, setIncludeAC] = useState(false)
  const [includeLights, setIncludeLights] = useState(true)
  const [includeWell, setIncludeWell] = useState(false)

  // Original calculation logic preserved verbatim from the legacy page-client.
  const result = useMemo(() => {
    // Floor of 10 W is below any compressor's running draw — a cold appliance
    // that pulls less than that is not on the circuit at all.
    const fridge = readQuantity(fridgeWatts, { min: 10, max: 2000 })
    const freezer = readQuantity(freezerWatts, { min: 10, max: 2000 })
    if (fridge === null || freezer === null) return null

    let runningWatts = fridge + freezer
    let startingWatts = fridge * 3 + freezer * 3

    if (includeLights) {
      runningWatts += 100
      startingWatts += 100
    }
    if (includeAC) {
      runningWatts += 1200
      startingWatts += 3600
    }
    if (includeWell) {
      runningWatts += 1000
      startingWatts += 3000
    }

    const recommendedWatts = Math.ceil(startingWatts * 1.2)

    let recommendedGenerator = ""
    let generatorSize = 0
    if (recommendedWatts < 3000) {
      recommendedGenerator = "2,000–3,000W portable inverter"
      generatorSize = 3000
    } else if (recommendedWatts < 5000) {
      recommendedGenerator = "3,500–5,000W portable"
      generatorSize = 5000
    } else if (recommendedWatts < 7500) {
      recommendedGenerator = "5,000–7,500W portable"
      generatorSize = 7500
    } else if (recommendedWatts < 10000) {
      recommendedGenerator = "7,500–10,000W portable or standby"
      generatorSize = 10000
    } else {
      recommendedGenerator = "10,000W+ standby"
      generatorSize = 12000
    }

    return { runningWatts, startingWatts, recommendedGenerator, generatorSize, recommendedWatts }
  }, [fridgeWatts, freezerWatts, includeAC, includeLights, includeWell])

  return (
    <ToolPageShell
      toolName={META.name}
      heroEyebrow={META.category}
      heroTitlePre="What generator keeps your"
      heroTitleItalic="fridge"
      heroTitlePost="alive in an outage?"
      heroSubtitle={META.mediumDescription}
      heroMeta={["⏱ ~30 sec", "Compressor surge × 1.2 margin"]}
      heroShare={<ToolShareBar title={META.name} />}
      heroRight={
        <StatBorderGrid
          stats={[
            { value: "3×", label: "Surge", note: "Single-speed compressor" },
            { value: "20%", label: "Margin", note: "Safety buffer" },
            { value: "2,000W", label: "Practical min", note: "Fridge only" },
            { value: "5,000W", label: "Fridge + freezer", note: "+ essentials" },
          ]}
          className="w-full max-w-md md:max-w-none md:w-auto mx-auto md:mx-0"
        />
      }
      faqs={META.faqs}
      relatedTools={buildRelated(META.relatedTools, "refrigerator-generator-size-calculator")}
      cta={{
        title: "Looking for a low-draw fridge that's easy to back up?",
        body: "Browse top-freezer refrigerators at Fridge.com — the lowest-wattage configuration on the market and easiest to keep alive on a small generator.",
        buttonLabel: "Browse top-freezer fridges",
        buttonHref: "/top-freezer-refrigerators",
      }}
    >
      <section className="bg-gray-50 py-8 md:py-12">
        <div className="max-w-[1120px] mx-auto px-6 md:px-8 space-y-6">
          <div className="grid lg:grid-cols-2 gap-6 lg:items-stretch">
            <div className="bg-white rounded-xl p-6 md:p-7 border border-gray-900 flex flex-col">
              <div className="flex items-center gap-3 mb-2">
                <span className="font-mono text-[12px] font-bold tracking-[0.15em] text-[#ff9951]">STEP 01</span>
                <div className="flex-1 h-[1px] bg-gray-300" />
              </div>
              <h2 className="text-gray-900 font-black" style={{ fontSize: 28, letterSpacing: "-0.025em", lineHeight: 1.05 }}>
                Five inputs.
                <br />
                Real generator size.
              </h2>
              <p className="text-[14px] text-gray-600 mt-2 leading-[1.5]">
                Sum running watts. Multiply each compressor by 3× for surge. Add 20% safety margin.
              </p>

              <div className="mt-7 space-y-5">
                <div>
                  <label className="block text-[11px] font-bold uppercase tracking-[0.12em] text-gray-900 mb-2">
                    Fridge running watts
                  </label>
                  <div className="relative">
                    <input
                      type="number"
                      min={10}
                      max={2000}
                      step="1"
                      value={fridgeWatts}
                      onChange={(e) => setFridgeWatts(e.target.value)}
                      className="tool-input-clean w-full px-4 py-4 text-[22px] font-bold rounded-lg outline-none border border-gray-300 focus:border-[#ff9951]"
                      style={{ fontVariantNumeric: "tabular-nums" }}
                    />
                    <span className="absolute right-4 top-1/2 -translate-y-1/2 text-[13px] font-mono text-gray-500">W</span>
                  </div>
                  <div className="text-[11px] text-gray-500 mt-1.5 flex items-center gap-1.5">
                    <Info className="w-3.5 h-3.5" /> From the inside-cabinet nameplate.
                  </div>
                </div>
                <div>
                  <label className="block text-[11px] font-bold uppercase tracking-[0.12em] text-gray-900 mb-2">
                    Freezer running watts
                  </label>
                  <div className="relative">
                    <input
                      type="number"
                      min={10}
                      max={2000}
                      step="1"
                      value={freezerWatts}
                      onChange={(e) => setFreezerWatts(e.target.value)}
                      className="tool-input-clean w-full px-4 py-4 text-[22px] font-bold rounded-lg outline-none border border-gray-300 focus:border-[#ff9951]"
                      style={{ fontVariantNumeric: "tabular-nums" }}
                    />
                    <span className="absolute right-4 top-1/2 -translate-y-1/2 text-[13px] font-mono text-gray-500">W</span>
                  </div>
                </div>
                <div className="space-y-3">
                  <label className="flex items-center gap-3 cursor-pointer">
                    <input type="checkbox" checked={includeLights} onChange={(e) => setIncludeLights(e.target.checked)} className="h-4 w-4 rounded border-gray-300" />
                    <span className="text-[14px] font-semibold text-gray-900">Essential LED lights (+100W)</span>
                  </label>
                  <label className="flex items-center gap-3 cursor-pointer">
                    <input type="checkbox" checked={includeAC} onChange={(e) => setIncludeAC(e.target.checked)} className="h-4 w-4 rounded border-gray-300" />
                    <span className="text-[14px] font-semibold text-gray-900">Window AC unit (+1,200W run / +3,600W surge)</span>
                  </label>
                  <label className="flex items-center gap-3 cursor-pointer">
                    <input type="checkbox" checked={includeWell} onChange={(e) => setIncludeWell(e.target.checked)} className="h-4 w-4 rounded border-gray-300" />
                    <span className="text-[14px] font-semibold text-gray-900">Well pump (+1,000W run / +3,000W surge)</span>
                  </label>
                </div>

                <div className="pt-2 text-[11px] uppercase tracking-[0.12em] text-gray-500 font-bold flex items-center gap-1.5">
                  <RefreshCw className="w-3 h-3" /> Updates live as you type
                </div>
              </div>
            </div>

            <div className="flex flex-col">
              <div className="rounded-2xl p-6 md:p-8 relative overflow-hidden text-white flex-1" style={{ background: "linear-gradient(135deg, #0469f0 0%, #0053c4 100%)" }}>
                <div aria-hidden className="absolute top-0 right-0 w-[340px] h-[340px] rounded-full pointer-events-none" style={{ background: "rgba(255,153,81,0.35)", filter: "blur(60px)", transform: "translate(25%, -35%)" }} />
                <div className="relative">
                  <div className="text-[11px] font-bold uppercase tracking-[0.15em] text-white/70 mb-8">
                    Recommended generator
                  </div>
                  <div className="font-black" style={{ fontSize: "clamp(48px, 6vw, 80px)", letterSpacing: "-0.045em", lineHeight: 0.95 }}>
                    {result?.generatorSize?.toLocaleString() ?? "—"}
                    <span className="text-[20px] md:text-[24px] font-black tracking-tighter text-white/60 ml-2">W</span>
                  </div>
                  <div className="mt-4 text-[14px] text-white/80 leading-[1.5]">
                    {result?.recommendedGenerator ?? "Enter your loads to size."}
                  </div>
                  <div className="grid grid-cols-2 gap-2 sm:gap-3 mt-6 md:mt-8">
                    <div className="rounded-lg p-3 sm:p-4 bg-white/10 border border-white/15">
                      <div className="text-[10px] uppercase tracking-[0.15em] text-white/60 font-bold mb-1">Running watts</div>
                      <div className="text-[18px] sm:text-[22px] font-black tracking-tight">{result?.runningWatts.toLocaleString() ?? "—"}</div>
                    </div>
                    <div className="rounded-lg p-3 sm:p-4 bg-white/10 border border-white/15">
                      <div className="text-[10px] uppercase tracking-[0.15em] text-white/60 font-bold mb-1">Starting watts</div>
                      <div className="text-[18px] sm:text-[22px] font-black tracking-tight">{result?.startingWatts.toLocaleString() ?? "—"}</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-white py-4 md:py-6">
        <ToolProductCarousel
          eyebrow="Step 02 · Pair with the right fridge"
          title="Top-freezer fridges — lowest wattage"
          description="Simpler refrigeration cycle, lower running watts, and softer compressor startup than larger configurations."
          collectionType="top-freezer"
          fallbackCollectionType="bottom-freezer"
        />
      </section>

      <section className="bg-gray-50 py-8 md:py-12 border-y border-gray-200">
        <div className="max-w-[1120px] mx-auto px-6 md:px-8">
          <div className="grid md:grid-cols-[1fr_1.3fr] gap-8 md:gap-12 items-start">
            <div>
              <div className="text-[11px] font-bold uppercase tracking-[0.15em] mb-3 text-[#ff9951]">Under the hood</div>
              <h2 className="text-gray-900 font-black" style={{ fontSize: "clamp(24px, 3vw, 36px)", letterSpacing: "-0.03em", lineHeight: 1.05 }}>
                <em className="italic text-[#ff9951]">Surge</em> watts. Safety margin.
              </h2>
              <p className="text-[14px] text-gray-600 mt-4 max-w-[400px] leading-[1.55]">
                Compressor surge multipliers come from manufacturer spec sheets (3× for single-speed). The 20% margin matches portable-generator manufacturer guidance to prevent overload trips.
              </p>
            </div>
            <ol className="list-none p-0 m-0 border-t border-gray-900">
              {[
                { n: "01", t: "Sum running watts", d: "Add fridge + freezer + each enabled essential. This is the steady-state load." },
                { n: "02", t: "Sum starting watts", d: "Compressor loads × 3 (single-speed surge). LED lights have no surge. Well pump and AC use 3× surge." },
                { n: "03", t: "Add 20% safety margin", d: "Required generator size = starting watts × 1.2. Round up to next standard portable or standby tier." },
                { n: "04", t: "Pick a generator class", d: "<3,000W: portable inverter. 3,500–5,000: standard portable. 5,000–7,500: tri-fuel portable. 7,500+: standby recommended." },
              ].map((s) => (
                <li key={s.n} className="grid grid-cols-[auto_1fr_auto] gap-4 sm:gap-7 items-center py-5 sm:py-6 border-b border-gray-900">
                  <div className="font-mono text-sm font-bold tracking-[0.05em] min-w-[28px] sm:min-w-[34px] text-[#ff9951]">{s.n}</div>
                  <div>
                    <h3 className="text-gray-900" style={{ fontSize: 22, fontWeight: 800, letterSpacing: "-0.02em" }}>{s.t}</h3>
                    <p className="mt-1.5 text-[14px] text-gray-600 leading-[1.55]">{s.d}</p>
                  </div>
                  <ArrowUpRight className="w-4 h-4 sm:w-5 sm:h-5 text-gray-500 flex-shrink-0" />
                </li>
              ))}
            </ol>
          </div>
        </div>
      </section>

      <section className="bg-white py-4 md:py-6">
        <ToolProductCarousel
          eyebrow="You might also like"
          title="Side-by-side fridges"
          description="Inverter compressors with low surge — friendlier to mid-size 5,000W portable generators."
          collectionType="side-by-side"
          fallbackCollectionType="bottom-freezer"
        />
      </section>
    </ToolPageShell>
  )
}
